import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import exerciseChoicesApi from 'api/rest/exercises/choices';
import PATHS from 'constants/paths';
import QUERY from 'constants/queryKeys';
import { resolveSubjectNumericId } from 'services/common/subjectParsers/subjectParsers';
import useExercisesStore from 'storages/exercises/exercises';
import useSubjectStore from 'storages/subject';

const FiltersCountContext = createContext(null);

const { Provider } = FiltersCountContext;

const toOptionalId = value => {
  if (value === 'all' || value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const choicesToFiltersItems = choices =>
  choices.map(({ value, label, count }) => ({
    name: label,
    id: value,
    exercisesCount: count,
  }));

const FiltersCountProvider = props => {
  const location = useLocation();
  const { subject } = useSubjectStore();
  const section = useExercisesStore(state => state.section);
  const subsection = useExercisesStore(state => state.subsection);
  const curriculum = useExercisesStore(state => state.curriculum);
  const isDone = useExercisesStore(state => state.isDone);
  /** Set when the filter dialog/form mounts so curriculum + isDone choices load lazily. */
  const [secondaryChoicesRequested, setSecondaryChoicesRequested] = useState(false);
  const subjectId = resolveSubjectNumericId(subject);
  const sectionId = toOptionalId(section);
  const subsectionId = toOptionalId(subsection);
  const onExercisesPage = PATHS.EXERCISES === location.pathname;
  const baseEnabled = onExercisesPage && !!subjectId;

  // Marks stay always-on (production parity + MarkExercise dialog refresh).
  // Curriculum / isDone only when chips need labels or the filter UI asked for them.
  const curriculumEnabled = baseEnabled && (secondaryChoicesRequested || !!curriculum?.length);
  const isDoneEnabled = baseEnabled && (secondaryChoicesRequested || !!isDone?.length);

  const marksQueryOptions = useMemo(
    () => ({
      enabled: baseEnabled,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2,
    }),
    [baseEnabled],
  );

  const curriculumQueryOptions = useMemo(
    () => ({
      enabled: curriculumEnabled,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2,
    }),
    [curriculumEnabled],
  );

  const isDoneQueryOptions = useMemo(
    () => ({
      enabled: isDoneEnabled,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2,
    }),
    [isDoneEnabled],
  );

  const { data: curriculumChoices, refetch: refetchCurriculum, isPreviousData: isCurriculumPrevious } = useQuery(
    [QUERY.GET_CURRICULUM_CHOICES, subjectId, sectionId, subsectionId],
    exerciseChoicesApi.getCurriculum({
      subject: subjectId,
      section: sectionId,
      subsection: subsectionId,
    }),
    curriculumQueryOptions,
  );

  const { data: markChoices, refetch: refetchMarks, isPreviousData: isMarksPrevious } = useQuery(
    [QUERY.GET_MARK_CHOICES, subjectId, sectionId, subsectionId, curriculum],
    exerciseChoicesApi.getMark({
      subject: subjectId,
      section: sectionId,
      subsection: subsectionId,
      curriculum,
    }),
    marksQueryOptions,
  );

  const { data: isDoneChoices, refetch: refetchIsDone, isPreviousData: isIsDonePrevious } = useQuery(
    [QUERY.GET_IS_DONE_CHOICES, subjectId, sectionId, subsectionId, curriculum],
    exerciseChoicesApi.getIsDone({
      subject: subjectId,
      section: sectionId,
      subsection: subsectionId,
      curriculum,
    }),
    isDoneQueryOptions,
  );

  const filters = useMemo(() => {
    const next = {};
    if (curriculumChoices) next.curriculum = choicesToFiltersItems(curriculumChoices);
    if (markChoices) next.userMarks = choicesToFiltersItems(markChoices);
    if (isDoneChoices) next.isDone = choicesToFiltersItems(isDoneChoices);
    return next;
  }, [curriculumChoices, markChoices, isDoneChoices]);

  const getMarksCount = useCallback(() => {
    if (onExercisesPage && subjectId) refetchMarks();
  }, [onExercisesPage, subjectId, refetchMarks]);

  const getIsDoneCount = useCallback(() => {
    if (onExercisesPage && subjectId) refetchIsDone();
  }, [onExercisesPage, subjectId, refetchIsDone]);

  const enableSecondaryChoices = useCallback(() => {
    setSecondaryChoicesRequested(true);
  }, []);

  /** Force-refresh mark / isDone / curriculum counts (e.g. when opening the filters drawer). */
  const revalidateFilterCounts = useCallback(() => {
    if (!onExercisesPage || !subjectId) return;
    setSecondaryChoicesRequested(true);
    refetchMarks();
    refetchIsDone();
    refetchCurriculum();
  }, [onExercisesPage, subjectId, refetchMarks, refetchIsDone, refetchCurriculum]);

  const updaters = useMemo(
    () => ({ getMarksCount, getIsDoneCount, enableSecondaryChoices, revalidateFilterCounts }),
    [getMarksCount, getIsDoneCount, enableSecondaryChoices, revalidateFilterCounts],
  );

  const value = useMemo(
    () => ({
      filters,
      updaters,
      isPreviousCurriculumCounts: isCurriculumPrevious,
      isPreviousMarksCounts: isMarksPrevious,
      isPreviousIsDoneCounts: isIsDonePrevious,
    }),
    [filters, updaters, isCurriculumPrevious, isMarksPrevious, isIsDonePrevious],
  );

  return <Provider value={value} {...props} />;
};

export const useFiltersCountContext = () => useContext(FiltersCountContext);

export default FiltersCountProvider;
